import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Business as BusinessIcon,
  AccountCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';

const Register = ({ onRegister }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Étape 1 : Informations de compte
    email: '',
    password: '',
    confirmPassword: '',
    
    // Étape 2 : Informations entreprise
    companyName: '',
    siret: '',
    vatNumber: '',
    industry: 'services',
    
    // Étape 3 : Coordonnées
    address: '',
    postalCode: '',
    city: '',
    country: 'France',
    phone: '',
    website: ''
  });

  const steps = ['Compte', 'Entreprise', 'Coordonnées'];

  const industries = [
    { value: 'services', label: 'Services / Consulting' },
    { value: 'it', label: 'Informatique / IT' },
    { value: 'construction', label: 'BTP / Construction' },
    { value: 'commerce', label: 'Commerce / Vente' },
    { value: 'artisan', label: 'Artisan' },
    { value: 'liberal', label: 'Profession Libérale' },
    { value: 'industrie', label: 'Industrie' },
    { value: 'other', label: 'Autre' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        if (!formData.email || !formData.email.includes('@')) {
          setError('Veuillez entrer une adresse email valide');
          return false;
        }
        if (formData.password.length < 8) {
          setError('Le mot de passe doit contenir au moins 8 caractères');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Les mots de passe ne correspondent pas');
          return false;
        }
        
        // Vérifier si l'email existe déjà
        const users = JSON.parse(localStorage.getItem('users') || '[]');
        if (users.find(u => u.email === formData.email)) {
          setError('Cet email est déjà utilisé');
          return false;
        }
        return true;
        
      case 1:
        if (!formData.companyName) {
          setError('Le nom de l\'entreprise est requis');
          return false;
        }
        return true;
        
      case 2:
        if (!formData.phone) {
          setError('Le numéro de téléphone est requis');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        setActiveStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Créer le compte utilisateur via l'API
      const userData = {
        email: formData.email,
        password: formData.password,
        name: formData.companyName, // Utiliser le nom de l'entreprise comme nom d'utilisateur
        role: 'USER'
      };

      await authService.register(userData);
      
      // L'utilisateur est maintenant connecté automatiquement (token sauvegardé par authService)
      // Afficher le tutoriel au premier accès (inscription Free)
      localStorage.setItem('onboardingTutorialPending', '1');
      onRegister();
      navigate('/dashboard');
    } catch (err) {
      console.error('Erreur lors de l\'inscription:', err);
      if (err.response?.status === 400) {
        setError(err.response.data.error || 'Un compte avec cet email existe déjà');
      } else {
        setError('Erreur lors de la création du compte. Vérifiez que le serveur est démarré.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountCircle /> Créez votre compte
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ces informations vous permettront de vous connecter à votre espace
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                autoComplete="email"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mot de passe"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
                helperText="Minimum 8 caractères"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirmer le mot de passe"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon /> Informations Entreprise
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ces informations apparaîtront sur vos devis et factures
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom de l'entreprise"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SIRET"
                value={formData.siret}
                onChange={(e) => handleChange('siret', e.target.value)}
                helperText="Facultatif"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numéro de TVA"
                value={formData.vatNumber}
                onChange={(e) => handleChange('vatNumber', e.target.value)}
                helperText="Facultatif"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Secteur d'activité</InputLabel>
                <Select
                  value={formData.industry}
                  onChange={(e) => handleChange('industry', e.target.value)}
                  label="Secteur d'activité"
                >
                  {industries.map(ind => (
                    <MenuItem key={ind.value} value={ind.value}>
                      {ind.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Coordonnées de Contact
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pour que vos clients puissent vous contacter
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Code Postal"
                value={formData.postalCode}
                onChange={(e) => handleChange('postalCode', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pays"
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Site Web"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                helperText="Facultatif"
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={(theme) => ({
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.palette.background.default,
        p: 2
      })}
    >
      <Card
        sx={{
          maxWidth: 700,
          width: '100%',
          background: 'linear-gradient(135deg, rgba(253, 251, 248, 0.98) 0%, rgba(240, 232, 223, 0.96) 100%)',
          border: `1px solid ${alpha('#8B8178', 0.25)}`,
          boxShadow: `0 20px 60px ${alpha('#5E564F', 0.18)}, 0 0 40px ${alpha('#C5A46D', 0.1)}`,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo SG DIGITAL */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #977F6B 0%, #8A7563 50%, #5E564F 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}>
              Facteio
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#5E564F', 0.8) }}>
              Créez votre compte professionnel
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Messages d'erreur */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Contenu de l'étape */}
          {renderStepContent(activeStep)}

          {/* Boutons de navigation */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
              sx={{
                flex: 1,
                color: '#5E564F',
                borderColor: alpha('#977F6B', 0.45),
                '&:hover': {
                  backgroundColor: alpha('#977F6B', 0.12),
                },
              }}
            >
              Retour
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              sx={(theme) => ({
                flex: 1,
                boxShadow: `0 10px 30px ${alpha(theme.palette.secondary.main, 0.22)}`,
                '&:hover': {
                  boxShadow: `0 14px 38px ${alpha(theme.palette.secondary.main, 0.28)}, 0 0 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              })}
            >
              {loading ? 'Création en cours...' : (activeStep === steps.length - 1 ? 'Créer mon compte' : 'Suivant')}
            </Button>
          </Box>

          {/* Lien vers connexion */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2">
              Vous avez déjà un compte ?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/login')}
                sx={{ cursor: 'pointer', color: '#8A6E57', fontWeight: 600 }}
              >
                Se connecter
              </Link>
            </Typography>
          </Box>

          {/* Footer SG DIGITAL */}
          <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: `1px solid ${alpha('#977F6B', 0.22)}` }}>
            <Typography variant="caption" color="text.secondary">
              Édité par <strong>SG DIGITAL</strong>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Register;
